import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { CmdLabel, CmdModal, CmdModalFooterButton } from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { AxiosResponse } from 'axios';
import Switch from './Switch';
import Textarea from './Textarea';
import CheckboxGroup from './CheckboxGroup';
import usePageWithForm from '../../../hooks/usePageWithForm';
import { customersAtom, selectedCustomersAtom, ICustomer } from '../../../store/recoil/customers';
import { getRequest } from '../../../Services/HttpService';

interface IFormProps {
  closeCallback: () => void;
  id: number;
}

export default function Form(props: IFormProps): JSX.Element {
  usePageWithForm();
  const { formatMessage: f } = useIntl();
  const [loader, setLoader] = React.useState<boolean>(false);
  const [customerLabel, setCustomerLabel] = React.useState('');
  const [editingData, setEditingData] = React.useState<{ [key: string]: any } | null>(null);
  const [newData, setNewData] = React.useState<{ [key: string]: any } | null>(null);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const customers = useRecoilValue<ICustomer[]>(customersAtom);

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'alarms.modal.rule.button.close' }),
      tooltip: f({ id: 'alarms.modal.recipient.close.tooltip' }),
      closeCallback: props.closeCallback,
    },
  ];

  const getData = async () => {
    const url = `/v1/gdpr/profile/history/${props.id}`;

    setLoader(true);

    const response: AxiosResponse | false = await getRequest(url, { returnAxiosResponse: true });
    let newR01 = 0;
    let newR03 = 0;
    let newR05 = 0;
    let newR06 = 0;
    let newR07 = 0;
    let newRide = 0;
    const newRideDetail: string[] = [];
    const newR05Detail: string[] = [];

    if (response && response.data && response.status === 200 && response.data.profile) {
      const fields = response.data.profile;
      if (fields.authReports) {
        const R01: any = Object.values(fields.authReports).find((i: any) => i.name === 'R01');
        const R03: any = Object.values(fields.authReports).find((i: any) => i.name === 'R03');
        const R05: any = Object.values(fields.authReports).find((i: any) => i.name === 'R05');
        const R06: any = Object.values(fields.authReports).find((i: any) => i.name === 'R06');
        const R07: any = Object.values(fields.authReports).find((i: any) => i.name === 'R07'); // fix bug
        newR01 = R01 ? (R01.enabled === true ? 1 : 0) : 0;
        newR03 = R03 ? (R03.enabled === true ? 1 : 0) : 0;
        newR05 = R05 ? (R05.enabled === true ? 1 : 0) : 0;
        newR06 = R06 ? (R06.enabled === true ? 1 : 0) : 0;
        newR07 = R07 ? (R07.enabled === true ? 1 : 0) : 0;

        if (R05 !== null && R05.enabled && R05.can) {
          Object.keys(R05.can).forEach((key: string) => {
            if (R05.can[key] === true) {
              newR05Detail.push(key);
            }
          });
        }
      }

      if (fields.authRides && fields.authRides[0] && fields.authRides[0].can) {
        Object.keys(fields.authRides[0].can).forEach((key: string) => {
          if (fields.authRides[0].can[key] === true) {
            newRideDetail.push(key);
          }
        });
      }

      newRide =
        fields.authRides && fields.authRides[0]
          ? fields.authRides[0].enabled === true
            ? 1
            : 0
          : 0;
      const inputs = {
        name: fields.name,
        customerId: fields.customerId,
        description: fields.description,
        map: fields.authMap && fields.authMap[0] ? (fields.authMap[0].enabled === true ? 1 : 0) : 0,
        R01: newR01,
        R03: newR03,
        R05: newR05,
        R06: newR06,
        R07: newR07,
        R05Detail: newR05Detail,
        correction:
          fields.authCorrections && fields.authCorrections[0]
            ? fields.authCorrections[0].enabled === true
              ? 1
              : 0
            : 0,
        ride: newRide,
        rideDetail: newRideDetail,
        refueling:
          fields.authRefuelings && fields.authRefuelings[0]
            ? fields.authRefuelings[0].enabled === true
              ? 1
              : 0
            : 0,
      };
      setNewData(inputs);
    }

    // orig data
    if (response && response.data && response.status === 200 && response.data.originalValues) {
      const fields = response.data.originalValues;
      const oldRideDetail: { [key: string]: string } = {};
      const oldR05Detail: { [key: string]: string } = {};
      const inputs: any = {};
      if (fields.name) {
        inputs.name = fields.name;
      }
      if (fields.description !== undefined) {
        inputs.description = fields.description;
      }
      if (fields.authReports) {
        const R01: any = Object.values(fields.authReports).find((i: any) => i.name === 'R01');
        const R03: any = Object.values(fields.authReports).find((i: any) => i.name === 'R03');
        const R05: any = Object.values(fields.authReports).find((i: any) => i.name === 'R05');
        const R06: any = Object.values(fields.authReports).find((i: any) => i.name === 'R06');
        const R07: any = Object.values(fields.authReports).find((i: any) => i.name === 'R07');
        const oldR01 = R01 ? (R01.enabled === true ? 1 : 0) : 0;
        const oldR03 = R03 ? (R03.enabled === true ? 1 : 0) : 0;
        const oldR05 = R05 ? (R05.enabled === true ? 1 : 0) : 0;
        const oldR06 = R06 ? (R06.enabled === true ? 1 : 0) : 0;
        const oldR07 = R07 ? (R07.enabled === true ? 1 : 0) : 0;
        if (oldR01 !== newR01) {
          inputs.R01 = oldR01;
        }
        if (oldR03 !== newR03) {
          inputs.R03 = oldR03;
        }
        if (oldR05 !== newR05) {
          inputs.R05 = oldR05;
        }
        if (oldR06 !== newR06) {
          inputs.R06 = oldR06;
        }
        if (oldR07 !== newR07) {
          inputs.R07 = oldR07;
        }

        if (R05 !== null && R05.enabled && R05.can) {
          Object.keys(R05.can).forEach((key: string) => {
            if (
              (R05.can[key] === true && !newR05Detail.includes(key)) ||
              (R05.can[key] === false && newR05Detail.includes(key))
            ) {
              if (R05.can[key] === true) {
                oldR05Detail[key] = f({ id: 'enum.alarms.status.active' });
              } else if (R05.can[key] === false) {
                oldR05Detail[key] = f({ id: 'enum.alarms.status.inactive' });
              }
            }
          });
        }

        inputs.R05Detail = oldR05Detail;
      }
      if (fields.authMap) {
        inputs.map =
          fields.authMap && fields.authMap[0] ? (fields.authMap[0].enabled === true ? 1 : 0) : 0;
      }
      if (fields.authCorrections) {
        inputs.correction =
          fields.authCorrections && fields.authCorrections[0]
            ? fields.authCorrections[0].enabled === true
              ? 1
              : 0
            : 0;
      }
      if (fields.authRefuelings) {
        inputs.refueling =
          fields.authRefuelings && fields.authRefuelings[0]
            ? fields.authRefuelings[0].enabled === true
              ? 1
              : 0
            : 0;
      }

      if (fields.authRides) {
        const oldRide = fields.authRides[0] ? (fields.authRides[0].enabled === true ? 1 : 0) : 0;
        if (newRide !== oldRide) {
          inputs.ride = oldRide;
        }

        // ride detail;
        if (fields.authRides[0] && fields.authRides[0].can) {
          Object.keys(fields.authRides[0].can).forEach((key: string) => {
            if (
              (fields.authRides[0].can[key] === true && !newRideDetail.includes(key)) ||
              (fields.authRides[0].can[key] === false && newRideDetail.includes(key))
            ) {
              if (fields.authRides[0].can[key] === true) {
                oldRideDetail[key] = f({ id: 'enum.alarms.status.active' });
              } else if (fields.authRides[0].can[key] === false) {
                oldRideDetail[key] = f({ id: 'enum.alarms.status.inactive' });
              }
            }
          });
        }
        inputs.rideDetail = oldRideDetail;
      }
      setEditingData(inputs);
    }
    setLoader(false);
  };

  const getCustomerLabel = () => {
    if (newData) {
      const customer = customers.find((i: any) => String(i.id) === String(newData.customerId));
      return customer ? customer.name : '';
    }
    return '';
  };

  React.useEffect(() => {
    if (selectedCustomers.length > 0 && customers.length > 0 && newData) {
      const label = getCustomerLabel();
      setCustomerLabel(label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomers, customers, newData]);

  React.useEffect(() => {
    if (props.id) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CmdModal
      id="profile-form-modal"
      title={f({ id: 'gdprProfile.modal.changeDetail.title' })}
      footerButtons={buttons}
      loader={loader}
      width="1454px"
    >
      <div>
        <div className="row">
          <div className="col-xxl-12">
            <CmdBox
              id="gdprProfile-basic-information"
              title={f({ id: 'alarms.section.basicInformation' })}
            >
              <div className="row">
                <div className="col-xxl-6">
                  <CmdLabel title={f({ id: 'form.title' })} value={newData ? newData.name : ''} />
                  {props.id && newData && newData.customerId && (
                    <CmdLabel title={f({ id: 'periodLock.customerId' })} value={customerLabel} />
                  )}
                </div>
                <div className="col-xxl-6">
                  <Textarea
                    name="description"
                    id="description"
                    label={f({ id: 'vehicle.note' })}
                    value={newData?.description}
                    isChangeDetail={editingData ? editingData.description : ''}
                    disabled={true}
                  />
                </div>
              </div>
            </CmdBox>
          </div>
        </div>

        <div className="row">
          <div className="col-xxl-6">
            <CmdBox
              id="gdprProfile-map-information"
              title={f({ id: 'menu.map' })}
              label={f({ id: 'gdprProfile.section.label.map' })}
            >
              <Switch
                id="map"
                value={newData?.map}
                label={f({ id: 'gdprProfile.map' })}
                isRight={true}
                disabled={true}
                isChangeDetail={
                  editingData && editingData.map !== undefined
                    ? editingData.map === 1
                      ? f({ id: 'enum.alarms.status.active' })
                      : f({ id: 'enum.alarms.status.inactive' })
                    : undefined
                }
                fullOpacity={true}
              />
            </CmdBox>
            <CmdBox
              id="gdpr-profile-reports-information"
              title={f({ id: 'menu.reports' })}
              label={f({ id: 'gdprProfile.section.label.reports' })}
            >
              <Switch
                id="R01"
                value={newData?.R01}
                label={f({ id: 'gdprProfile.R01' })}
                isRight={true}
                disabled={true}
                isChangeDetail={
                  editingData && editingData.R01 !== undefined
                    ? editingData.R01 === 1
                      ? f({ id: 'enum.alarms.status.active' })
                      : f({ id: 'enum.alarms.status.inactive' })
                    : undefined
                }
                fullOpacity={true}
              />
              <Switch
                id="R03"
                value={newData?.R03}
                isRight={true}
                label={f({ id: 'gdprProfile.R03' })}
                disabled={true}
                fullOpacity={true}
                isChangeDetail={
                  editingData && editingData.R03 !== undefined
                    ? editingData.R03 === 1
                      ? f({ id: 'enum.alarms.status.active' })
                      : f({ id: 'enum.alarms.status.inactive' })
                    : undefined
                }
              />
              <Switch
                id="R05"
                value={newData?.R05}
                isRight={true}
                label={f({ id: 'gdprProfile.R05' })}
                disabled={true}
                fullOpacity={true}
                isChangeDetail={
                  editingData && editingData.R05 !== undefined
                    ? editingData.R05 === 1
                      ? f({ id: 'enum.alarms.status.active' })
                      : f({ id: 'enum.alarms.status.inactive' })
                    : undefined
                }
              />
              <CheckboxGroup
                key="R05Detail"
                id="R05Detail"
                name="R05Detail"
                label=""
                value={newData?.R05Detail}
                options={[
                  {
                    value: 'showPrivateData',
                    item: f({ id: 'gdprProfile.showPrivateData' }),
                  },
                ]}
                disabled={true}
                gridTemplateColumns=" 1fr"
                isChangeDetail={editingData ? editingData.R05Detail : {}}
                fullOpacity={true}
              />
              <Switch
                id="R06"
                value={newData?.R06}
                isRight={true}
                label={f({ id: 'gdprProfile.R06' })}
                disabled={true}
                fullOpacity={true}
                isChangeDetail={
                  editingData && editingData.R06 !== undefined
                    ? editingData.R06 === 1
                      ? f({ id: 'enum.alarms.status.active' })
                      : f({ id: 'enum.alarms.status.inactive' })
                    : undefined
                }
              />
              <Switch
                id="R07"
                value={newData?.R07}
                isRight={true}
                label={f({ id: 'gdprProfile.R07' })}
                disabled={true}
                fullOpacity={true}
                isChangeDetail={
                  editingData && editingData.R07 !== undefined
                    ? editingData.R07 === 1
                      ? f({ id: 'enum.alarms.status.active' })
                      : f({ id: 'enum.alarms.status.inactive' })
                    : undefined
                }
              />
            </CmdBox>
            <CmdBox
              id="gdprProfile-corrections-information"
              title={f({ id: 'menu.corrections' })}
              label={f({ id: 'gdprProfile.section.label.correction' })}
            >
              <Switch
                id="correction"
                value={newData?.correction}
                label={f({ id: 'gdprProfile.correction' })}
                isRight={true}
                disabled={true}
                isChangeDetail={
                  editingData && editingData.correction !== undefined
                    ? editingData.correction === 1
                      ? f({ id: 'enum.alarms.status.active' })
                      : f({ id: 'enum.alarms.status.inactive' })
                    : undefined
                }
                fullOpacity={true}
              />
            </CmdBox>
          </div>

          <div className="col-xxl-6">
            <CmdBox
              id="gdprProfile-ride-information"
              title={f({ id: 'menu.overviewTours' })}
              label={f({ id: 'gdprProfile.section.label.rides' })}
            >
              <Switch
                id="ride"
                isRight={true}
                value={newData?.ride}
                label={f({ id: 'gdprProfile.ride' })}
                disabled={true}
                isChangeDetail={
                  editingData && editingData.ride !== undefined
                    ? editingData.ride === 1
                      ? f({ id: 'enum.alarms.status.active' })
                      : f({ id: 'enum.alarms.status.inactive' })
                    : undefined
                }
                fullOpacity={true}
              />
              <CheckboxGroup
                value={newData ? newData.rideDetail : []}
                fullOpacity={true}
                key="rideDetail-1"
                id="rideDetail-1"
                name="rideDetail"
                isChangeDetail={editingData ? editingData.rideDetail : {}}
                label=""
                options={[
                  {
                    value: 'showPrivateData',
                    item: f({ id: 'gdprProfile.showPrivateData' }),
                  },
                ]}
                disabled={true}
                gridTemplateColumns=" 1fr"
              />
              <div style={{ marginLeft: '25px', marginTop: '-25px' }}>
                <CheckboxGroup
                  value={newData ? newData.rideDetail : []}
                  fullOpacity={true}
                  key="rideDetail-2"
                  id="rideDetail-2"
                  name="rideDetail"
                  isChangeDetail={editingData ? editingData.rideDetail : {}}
                  label=""
                  options={[
                    { value: 'showEvents', item: f({ id: 'gdprProfile.showEvents' }) },
                    { value: 'showAlarms', item: f({ id: 'gdprProfile.showAlarms' }) },
                    {
                      value: 'showWaypoints',
                      item: f({ id: 'gdprProfile.showWaypoints' }),
                    },
                  ]}
                  disabled={true}
                  gridTemplateColumns=" 1fr"
                />
              </div>
              <p
                style={{
                  marginLeft: '70px',
                  fontWeight: 'bold',
                  fontSize: '11px',
                  color: 'rgb(179, 179, 179)',
                  marginTop: '-10px',
                }}
              >
                {f({ id: 'gdprProfile.tripsOverview.note' })}
              </p>
              <div style={{ marginLeft: '25px' }}>
                <CheckboxGroup
                  fullOpacity={true}
                  value={newData ? newData.rideDetail : []}
                  key="rideDetail-3"
                  id="rideDetail-3"
                  name="rideDetail"
                  isChangeDetail={editingData ? editingData.rideDetail : {}}
                  label=""
                  options={[
                    {
                      value: 'showRideDetail',
                      item: f({ id: 'gdprProfile.showRideDetail' }),
                    },
                    { value: 'showRide', item: f({ id: 'gdprProfile.showRide' }) },
                  ]}
                  disabled={true}
                  gridTemplateColumns=" 1fr"
                />
              </div>

              <CheckboxGroup
                fullOpacity={true}
                value={newData ? newData.rideDetail : []}
                key="rideDetail"
                id="rideDetail"
                name="rideDetail"
                isChangeDetail={editingData ? editingData.rideDetail : {}}
                label=""
                options={[
                  { value: 'changeDriver', item: f({ id: 'gdprProfile.changeDriver' }) },
                  {
                    value: 'changeRidePrivacy',
                    item: f({ id: 'gdprProfile.changeRidePrivacy' }),
                  },
                ]}
                disabled={true}
                gridTemplateColumns=" 1fr"
              />
            </CmdBox>
            <CmdBox
              id="gdprProfile-status-information"
              title={f({ id: 'menu.refuelings' })}
              label={f({ id: 'gdprProfile.section.label.refueling' })}
            >
              <Switch
                id="refueling"
                value={newData?.refueling}
                label={f({ id: 'gdprProfile.refueling' })}
                isRight={true}
                disabled={true}
                isChangeDetail={
                  editingData && editingData.refueling !== undefined
                    ? editingData.refueling === 1
                      ? f({ id: 'enum.alarms.status.active' })
                      : f({ id: 'enum.alarms.status.inactive' })
                    : undefined
                }
                fullOpacity={true}
              />
            </CmdBox>
          </div>
        </div>
      </div>
    </CmdModal>
  );
}
